import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import { Search, PencilSquare, Trash, PlusCircle, Backspace, CashStack, PersonBadge, Copy } from "react-bootstrap-icons" // Iconos de Bootstrap
import { AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"
import formatCurrency from "../helpers/currencyHelper"
import FormModal from "../components/modals/FormModal"
import TableManagementThreeButtons from "../components/tables/TableManagementThreeButtoms";
import SearchSelect from '../components/form/SearchSelect'
import PayrollsService from "../services/PayrollsService"
import UsersService from "../services/UsersService"
import PayrollsForm from "./PayrollsForm"

const Payrolls = () => {
    const intl = useIntl()
    const [payrollId, setPayrollId] = useState(null)
    const [payrolls, setPayrolls] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("date")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    const [usersList, setUsersList] = useState([])
    const [selectedUser, setSelectedUser] = useState('')

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch users
    const fetchUsers = async () => {
        try {
            const response = await UsersService.getUsers(1, '', 2000)
            setUsersList(response.data.users)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch general payrolls
    const fetchPayrolls = async () => {
        try {
            const response = await PayrollsService.getPayrolls(currPage, search, perPage, sortBy, sortOrder, selectedUser)
            setPayrolls(response.data.payrolls)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchPayrolls()
    }

    const handleSelectUserChange = (e) => {
        setSelectedUser(e.target.value)
        setCurrPage(1)
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setSelectedUser('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // fetch users when start
    useEffect(() => {
        fetchUsers()
    }, [])

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchPayrolls()
    }, [currPage, perPage, search, sortBy, sortOrder, selectedUser])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="payrollsAdd" defaultMessage="Add payroll" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="payrollsEdit" defaultMessage="Edit payroll" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="payrollsDelete" defaultMessage="Delete payroll" />,
                icon: <Trash />,
            },
            duplicate: {
                title: <FormattedMessage id="textDuplicate" defaultMessage="Duplicate expense" />,
                icon: <Copy />,
            },
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setPayrollId(type === 'add' ? false : data._id) // Si 'add', incomeid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'userId',
            name: <FormattedMessage id="payrollsUser" defaultMessage="User" />,
            selector: (row) => row.userId.name,
            sortable: true,
        },
        {
            id: 'date',
            name: <FormattedMessage id="payrollsDate" defaultMessage="Payroll date" />,
            selector: (row) => formatDate(row.date, 'DD/MM/YYYY'),
            sortable: true,
        },
        {
            id: 'netSalary',
            name: <FormattedMessage id="payrollsNetSalary" defaultMessage="Payroll Net Salary" />,
            selector: (row) => formatCurrency(row.netSalary),
            right: "true"
        },
        {
            id: 'irpf',
            name: <FormattedMessage id="payrollsIrpf" defaultMessage="IRPF" />,
            selector: (row) => formatCurrency(row.irpf),
            right: "true"
        }, {
            id: 'ss',
            name: <FormattedMessage id="payrollsSs" defaultMessage="SS" />,
            selector: (row) => formatCurrency(row.ss),
            right: "true"
        }, {
            id: 'grossSalary',
            name: <FormattedMessage id="payrollsGrossSalary" defaultMessage="Gross salary" />,
            selector: (row) => formatCurrency(row.grossSalary),
            right: "true"
        }
    ]

    return (
        <Container fluid className="p-0">
            <Row className="p-1 bg-secondary">
                <Col>
                    <InputGroup>
                        <InputGroup.Text><PersonBadge /></InputGroup.Text>
                        <SearchSelect
                            fieldName={'userId'}
                            selected={selectedUser}
                            onChange={handleSelectUserChange}
                            options={usersList}
                        />
                        <InputGroup.Text><Search /></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search payrolls..." })}
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Button onClick={handleSearchClean} variant="light">
                            <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                        </Button>

                        <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                            <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {isLoaded ? (
                <TableManagementThreeButtons
                    icon={<CashStack />}
                    title={<FormattedMessage id="navPayrolls" defaultMessage="Payrolls" />}
                    columns={columns}
                    data={payrolls}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={PayrollsForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={payrollId}
                action={modalAction}
            />

        </Container>
    )
}

export default Payrolls